import { Button } from 'cpool-components/input/Button';
import type { ApplicantCreation } from 'cpool-schemas';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuPlus } from 'react-icons/lu';

import { DispositionsKredit } from './CreditKinds/DispositionsKredit';
import { GeschaeftsKredit } from './CreditKinds/GeschaeftsKredit';
import { Immobiliendarlehen } from './CreditKinds/Immobiliendarlehen';
import { KontokorrentKredit } from './CreditKinds/KontokorrentKredit';
import { KreditKarte } from './CreditKinds/KreditKarte';
import { Leasing } from './CreditKinds/Leasing';
import { RatenKredit } from './CreditKinds/RatenKredit';
import { SonstigeVerbindlichkeit } from './CreditKinds/SonstigeVerbindlichkeit';
import { useApplicantAttribution } from '../../util/hooks/useApplicantAttribution';
import type { LoanType } from '../../util/types';
import {
    DISPOSITIONSKREDIT,
    GESCHAEFTSKREDIT,
    IMMOBILIENDARLEHEN,
    KONTOKORRENTKREDIT,
    KREDITKARTE,
    LEASING,
    LoanTypeKeys,
    RATENKREDIT,
    SONSTIGE_VERBINDLICHKEITEN,
} from '../../util/types';
import {
    getDispositionskreditDefaultValues,
    getGeschaeftskreditDefaultValues,
    getImmobiliendarlehenDefaultValues,
    getKontokorrentkreditDefaultValues,
    getKreditkarteDefaultValues,
    getLeasingDefaultValues,
    getRatenkreditDefaultValues,
    getSonstigeVerbindlichkeitDefaultValues,
} from '../../util/values/creditKinds';

export const ExistingCredits: React.FC = () => {
    const { t } = useTranslation();
    const { control } = useFormContext<ApplicantCreation>();
    const attribution = useApplicantAttribution();

    const dispositionskredite = useFieldArray({ control, name: DISPOSITIONSKREDIT });
    const geschaeftskredite = useFieldArray({ control, name: GESCHAEFTSKREDIT });
    const immobiliendarlehen = useFieldArray({ control, name: IMMOBILIENDARLEHEN });
    const kontokorrentkredite = useFieldArray({ control, name: KONTOKORRENTKREDIT });
    const kreditkarten = useFieldArray({ control, name: KREDITKARTE });
    const leasings = useFieldArray({ control, name: LEASING });
    const ratenkredite = useFieldArray({ control, name: RATENKREDIT });
    const sonstigeVerbindlichkeiten = useFieldArray({ control, name: SONSTIGE_VERBINDLICHKEITEN });

    const appendCredit = (creditType: LoanType) => {
        switch (creditType) {
            case DISPOSITIONSKREDIT:
                dispositionskredite.append(getDispositionskreditDefaultValues(attribution));
                break;
            case KREDITKARTE:
                kreditkarten.append(getKreditkarteDefaultValues(attribution));
                break;
            case KONTOKORRENTKREDIT:
                kontokorrentkredite.append(getKontokorrentkreditDefaultValues(attribution));
                break;
            case LEASING:
                leasings.append(getLeasingDefaultValues(attribution));
                break;
            case RATENKREDIT:
                ratenkredite.append(getRatenkreditDefaultValues(attribution));
                break;
            case GESCHAEFTSKREDIT:
                geschaeftskredite.append(getGeschaeftskreditDefaultValues(attribution));
                break;
            case SONSTIGE_VERBINDLICHKEITEN:
                sonstigeVerbindlichkeiten.append(getSonstigeVerbindlichkeitDefaultValues(attribution));
                break;
            case IMMOBILIENDARLEHEN:
                immobiliendarlehen.append(getImmobiliendarlehenDefaultValues(attribution));
                break;
            default:
                break;
        }
    };

    return (
        <div className="flex w-full flex-col gap-2">
            <div className="flex flex-col gap-2">
                    {dispositionskredite.fields.map((field, index) => (
                        <DispositionsKredit
                            key={field.id}
                            index={index}
                            onRemove={() => dispositionskredite.remove(index)}
                        />
                    ))}
                    {kreditkarten.fields.map((field, index) => (
                        <KreditKarte key={field.id} index={index} onRemove={() => kreditkarten.remove(index)} />
                    ))}
                    {kontokorrentkredite.fields.map((field, index) => (
                        <KontokorrentKredit
                            key={field.id}
                            index={index}
                            onRemove={() => kontokorrentkredite.remove(index)}
                        />
                    ))}
                    {ratenkredite.fields.map((field, index) => (
                        <RatenKredit key={field.id} index={index} onRemove={() => ratenkredite.remove(index)} />
                    ))}
                    {geschaeftskredite.fields.map((field, index) => (
                        <GeschaeftsKredit
                            key={field.id}
                            index={index}
                            onRemove={() => geschaeftskredite.remove(index)}
                        />
                    ))}
                    {sonstigeVerbindlichkeiten.fields.map((field, index) => (
                        <SonstigeVerbindlichkeit
                            key={field.id}
                            index={index}
                            onRemove={() => sonstigeVerbindlichkeiten.remove(index)}
                        />
                    ))}
                    {immobiliendarlehen.fields.map((field, index) => (
                        <Immobiliendarlehen
                            key={field.id}
                            index={index}
                            onRemove={() => immobiliendarlehen.remove(index)}
                        />
                    ))}
                    {leasings.fields.map((field, index) => (
                        <Leasing key={field.id} index={index} onRemove={() => leasings.remove(index)} />
                    ))}
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2 py-2">
                {LoanTypeKeys.map((creditType) => (
                    <Button
                        key={creditType}
                        variant="secondary"
                        type="button"
                        onClick={() => appendCredit(creditType)}
                        iconLeft={<LuPlus className="w-3 h-3" />}
                    >
                        {t(`EXISTING_LOANS.TYPES.${creditType}`)}
                    </Button>
                ))}
            </div>
        </div>
    );
};
